import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Upload, ChevronDown, MapPin } from "lucide-react";
import { api, fileUrl } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

function AcademyRow({ a, reload }) {
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [assignEmail, setAssignEmail] = useState("");
  const [loc, setLoc] = useState({ name: "", city: "", state: "" });
  const mainRef = useRef();
  const secRef = useRef();

  const uploadLogo = async (ref, which) => {
    const file = ref.current?.files?.[0];
    if (!file) return toast.error("Choose a file");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: up } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = fileUrl(up.storage_path);
      const body = which === "main"
        ? { main_logo_file_id: up.id, main_logo_url: url }
        : { secondary_logo_file_id: up.id, secondary_logo_url: url };
      await api.patch(`/admin/academies/${a.id}/branding`, body);
      toast.success("Logo uploaded & assigned");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const toggle = async (field) => {
    await api.put(`/academies/${a.id}`, { ...a, [field]: !a[field] });
    reload();
  };
  const addLoc = async () => {
    if (!loc.name) return toast.error("Location name required");
    await api.post(`/admin/academies/${a.id}/locations`, loc);
    setLoc({ name: "", city: "", state: "" });
    toast.success("Location added");
    reload();
  };
  const assign = async () => {
    if (!assignEmail) return;
    try {
      await api.patch(`/admin/academies/${a.id}/assign-owner`, { email: assignEmail });
      toast.success("Gym owner assigned");
      setAssignEmail("");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed — user must exist"); }
  };
  const sendWelcome = async () => {
    try {
      const { data } = await api.post(`/admin/academies/${a.id}/send-welcome`);
      toast.success(`Welcome email sent to ${data.sent_to.join(", ")}`);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send welcome email"); }
  };

  return (
    <div className="bg-[#121216] border border-white/10 rounded-lg overflow-hidden">
      <button data-testid={`academy-expand-${a.id}`} onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-5 py-4 hover:bg-white/5">
        <div className="flex items-center gap-3 text-left">
          <img src={a.main_logo_url || "/paragon-logo.jpeg"} alt="" className="h-10 w-10 rounded-full object-contain" />
          <div>
            <p className="font-heading font-bold uppercase text-white">{a.name}</p>
            <p className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
              {a.code} · {[a.city, a.state].filter(Boolean).join(", ") || "—"} · {a.locations?.length || 0} loc
              {!a.uses_master_logo && !a.main_logo_url ? " · LOGO PENDING" : ""}
            </p>
          </div>
        </div>
        <ChevronDown className={`h-5 w-5 text-zinc-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 border-t border-white/10 pt-4 grid md:grid-cols-2 gap-6">
          <div>
            <p className="font-mono text-xs uppercase tracking-widest text-zinc-400 mb-2">Branding / Artwork</p>
            <div className="flex items-center gap-3 mb-3">
              <input ref={mainRef} type="file" data-testid={`main-logo-file-${a.id}`} accept="image/*,.svg,.pdf" className="text-xs text-zinc-400" />
              <button data-testid={`upload-main-${a.id}`} disabled={uploading} onClick={() => uploadLogo(mainRef, "main")} className="btn-primary inline-flex items-center gap-1.5 px-3 py-2 rounded-sm text-xs"><Upload className="h-3.5 w-3.5" /> Main</button>
            </div>
            <div className="flex items-center gap-3 mb-3">
              <input ref={secRef} type="file" accept="image/*,.svg,.pdf" className="text-xs text-zinc-400" />
              <button onClick={() => uploadLogo(secRef, "secondary")} className="btn-outline inline-flex items-center gap-1.5 px-3 py-2 rounded-sm text-xs"><Upload className="h-3.5 w-3.5" /> Secondary</button>
            </div>
            <label className="flex items-center gap-2 text-sm text-zinc-300 mt-2">
              <input type="checkbox" checked={!!a.uses_master_logo} onChange={() => toggle("uses_master_logo")} /> Uses Paragon master logo
            </label>
            <label className="flex items-center gap-2 text-sm text-zinc-300 mt-2">
              <input type="checkbox" data-testid={`wholesale-toggle-${a.id}`} checked={!!a.wholesale_enabled} onChange={() => toggle("wholesale_enabled")} /> Wholesale enabled
            </label>
          </div>
          <div>
            <p className="font-mono text-xs uppercase tracking-widest text-zinc-400 mb-2">Locations</p>
            <ul className="space-y-1 mb-3">
              {(a.locations || []).map((l, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-zinc-300"><MapPin className="h-3 w-3 text-emerald-400" /> {l.name} {l.city ? `· ${l.city}` : ""}</li>
              ))}
              {(!a.locations || a.locations.length === 0) && <li className="text-zinc-600 text-sm">No locations</li>}
            </ul>
            <div className="flex gap-2 mb-4">
              <Input value={loc.name} onChange={(e) => setLoc({ ...loc, name: e.target.value })} placeholder="Location name" className="bg-[#0A0A0C] border-white/15 text-sm" />
              <button data-testid={`add-location-${a.id}`} onClick={addLoc} className="btn-outline px-3 py-2 rounded-sm text-xs shrink-0">Add</button>
            </div>
            <p className="font-mono text-xs uppercase tracking-widest text-zinc-400 mb-2">Assign Gym Owner</p>
            <div className="flex gap-2">
              <Input value={assignEmail} onChange={(e) => setAssignEmail(e.target.value)} placeholder="existing user email" className="bg-[#0A0A0C] border-white/15 text-sm" />
              <button data-testid={`assign-owner-${a.id}`} onClick={assign} className="btn-green px-3 py-2 rounded-sm text-xs shrink-0">Assign</button>
            </div>
            <Link to={`/admin/analytics?academy=${a.id}`} className="text-yellow-400 font-mono text-xs uppercase inline-block mt-4">View Analytics →</Link>
            <div className="mt-4">
              <button data-testid={`send-welcome-${a.id}`} onClick={sendWelcome} className="btn-green px-3 py-2 rounded-sm text-xs">Send Welcome Email</button>
              {a.welcome_sent_at && <p data-testid={`welcome-sent-${a.id}`} className="font-mono text-[10px] text-zinc-500 mt-1">Last sent {new Date(a.welcome_sent_at).toLocaleString()}</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminAcademies() {
  const [academies, setAcademies] = useState([]);
  const [form, setForm] = useState({ name: "", city: "", state: "", contact_name: "", email: "" });
  const [gym, setGym] = useState({ name: "", email: "", password: "", academy_id: "" });

  const load = () => api.get("/academies").then(({ data }) => setAcademies(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const createAcademy = async () => {
    if (!form.name) return toast.error("Academy name required");
    await api.post("/academies", form);
    toast.success("Academy created");
    setForm({ name: "", city: "", state: "", contact_name: "", email: "" });
    load();
  };
  const createGym = async () => {
    if (!gym.email || !gym.password || !gym.academy_id) return toast.error("Email, password and academy required");
    try { await api.post("/admin/gym-owners", gym); toast.success("Gym owner account created"); setGym({ name: "", email: "", password: "", academy_id: "" }); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="px-5 lg:px-8 py-8">
      <p className="font-mono text-yellow-400 uppercase tracking-widest text-xs">Network</p>
      <h1 className="font-heading font-black uppercase text-4xl text-white mt-1 mb-6">Academies &amp; Affiliates</h1>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-3">
          {academies.map((a) => <AcademyRow key={a.id} a={a} reload={load} />)}
        </div>

        <div className="space-y-6">
          <div className="bg-[#121216] border border-white/10 rounded-lg p-5">
            <h3 className="font-heading font-bold uppercase text-white mb-3">Add Academy</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label className="text-zinc-400">Name</Label><Input data-testid="academy-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0A0A0C] border-white/15 mt-1" /></div>
              <div><Label className="text-zinc-400">City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="bg-[#0A0A0C] border-white/15 mt-1" /></div>
              <div><Label className="text-zinc-400">State</Label><Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="bg-[#0A0A0C] border-white/15 mt-1" /></div>
            </div>
            <button data-testid="create-academy" onClick={createAcademy} className="btn-primary px-5 py-2.5 rounded-sm mt-4 text-sm">Create Academy</button>
          </div>

          <div className="bg-[#121216] border border-white/10 rounded-lg p-5">
            <h3 className="font-heading font-bold uppercase text-white mb-3">Add Gym Owner Account</h3>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-zinc-400">Name</Label><Input value={gym.name} onChange={(e) => setGym({ ...gym, name: e.target.value })} className="bg-[#0A0A0C] border-white/15 mt-1" /></div>
              <div><Label className="text-zinc-400">Academy</Label>
                <select data-testid="gym-academy" value={gym.academy_id} onChange={(e) => setGym({ ...gym, academy_id: e.target.value })} className="w-full bg-[#0A0A0C] border border-white/15 rounded px-2 py-2 mt-1 text-sm">
                  <option value="">Select…</option>
                  {academies.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
              <div><Label className="text-zinc-400">Email</Label><Input data-testid="gym-email" value={gym.email} onChange={(e) => setGym({ ...gym, email: e.target.value })} className="bg-[#0A0A0C] border-white/15 mt-1" /></div>
              <div><Label className="text-zinc-400">Password</Label><Input data-testid="gym-password" value={gym.password} onChange={(e) => setGym({ ...gym, password: e.target.value })} className="bg-[#0A0A0C] border-white/15 mt-1" /></div>
            </div>
            <button data-testid="create-gym" onClick={createGym} className="btn-green px-5 py-2.5 rounded-sm mt-4 text-sm">Create Gym Owner</button>
          </div>
        </div>
      </div>
    </div>
  );
}
